import React, { useCallback, useState, useEffect } from "react";
import "./OrderSelectionList.css";
import { Checkbox } from "primereact/checkbox";
import { Field, Form } from "react-final-form";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import {
  fetchCountriesAndStates,
  fetchStateTitleTypes
} from "../../services/local-storage";
import { Dropdown } from "primereact/dropdown";
import {
  allOrderTypes,
  groupedOrderInitialTypes,
  groupedOrderTypes
} from "../../services/constants";
import { areArraysEqual } from "../../services/formatting";

const OrderSelectionList = ({
  carrier,
  updateSelectedOrders,
  currentOrders,
  packetOrders,
  xTitleSelected,
  setXTitleSelected,
  xTitleChecked,
  setXTitleChecked,
  financed,
  completedOrderTypes,
  setCompletedOrderTypes,
  stepperRef,
  selectedOrders,
  titleTypes,
  titleState,
  setTitleState,
  titleType,
  setTitleType,
  mode,
  changeMode,
  revertMode = () => {}
}) => {
  const [allowedTitleTypes, setAllowedTitleTypes] = useState([]);
  const [unplacedOrderTypes, setUnplacedOrderTypes] = useState([]);
  const [availableOrderTypes, setAvailableOrderTypes] = useState([]);

  const stateTitleTypes = fetchStateTitleTypes();
  const countriesAndStates = fetchCountriesAndStates();
  const allCountries = countriesAndStates.countries;
  const mainCountry = allCountries?.find(
    country => country.active && country.countryInitials === "US"
  );
  const states = countriesAndStates?.states?.filter(
    state =>
      state.countryId === mainCountry?.countryId &&
      stateTitleTypes.find(states => state.stateId === states.stateId)
  );

  // remove order types unavailable to the carrier
  useEffect(() => {
    let o;
    if (carrier?.orderGrouping) {
      // if order grouping is enabled, show all grouped orders, unless xTitle or lienLetter is explicitly inactive
      o = allOrderTypes.filter(orderType => {
        return (
          (mode === "create" &&
            groupedOrderInitialTypes.includes(orderType.name)) ||
          (mode !== "create" && groupedOrderTypes.includes(orderType.name))
        );
      });
      if (carrier?.inactiveOrders?.includes("Title Transfer")) {
        o = o.filter(orderType => orderType.name !== "xTitle");
      }
    } else if (!carrier?.additionalOrderTypes) {
      o = allOrderTypes.filter(
        ot => ot.name === "loanPayoff" || ot.name === "letterOfGuarantee"
      );
    } else {
      o = allOrderTypes.filter(orderType => {
        return !carrier.inactiveOrders?.includes(orderType.label);
      });
    }
    if (
      !areArraysEqual(
        availableOrderTypes.map(o => o.name),
        o.map(o => o.name)
      )
    ) {
      setAvailableOrderTypes(o);
    }
  }, [carrier, availableOrderTypes, setAvailableOrderTypes, mode]);

  // remove order types that have already been placed and are pending
  // add "(re-order)" to the label of orders that have been placed and are fulfilled or cancelled
  useEffect(() => {
    const allOrderLabels = availableOrderTypes.map(
      orderType => orderType?.name
    );
    const currentOrderLabels = [];
    const completedOrderLabels = [];
    Object.keys(currentOrders).map(order => {
      const fullOrderName = allOrderTypes.find(ot => ot?.name === order)?.label;
      const fullOrderAlias = allOrderTypes.find(ot => ot?.name === order)
        ?.alias;
      const packetOrder = packetOrders?.find(po => {
        if (po?.type === fullOrderName || po?.type === fullOrderAlias) {
          return po;
        }
        return null;
      });
      if (packetOrder) {
        if (
          packetOrder.status === "fulfilled" ||
          packetOrder.status === "cancelled"
        ) {
          completedOrderLabels.push(order);
        } else {
          currentOrderLabels.push(order);
        }
      } else {
        currentOrderLabels.push(order);
      }
      return null;
    });
    const unplacedOrders = allOrderLabels.filter(orderType => {
      return (
        !currentOrderLabels.includes(orderType) &&
        !completedOrderLabels.includes(orderType)
      );
    });
    const u = [];
    unplacedOrders.forEach(orderType => {
      u.push(
        availableOrderTypes.find(ot => {
          if (ot.name === orderType || ot.label === orderType) {
            if (ot.label === "Title Image") {
              return {
                ...ot,
                label: "Copy of Title"
              };
            }
            return ot;
          }
          return null;
        })
      );
    });
    if (unplacedOrderTypes?.length !== Object.keys(u)?.length) {
      setUnplacedOrderTypes(u);
    }

    const completedOrders = completedOrderLabels;
    const c = [];
    completedOrders.forEach(orderType => {
      c.push(
        availableOrderTypes.find(ot => {
          if (ot.name === orderType || ot.label === orderType) {
            if (ot.label === "Title Image") {
              return {
                ...ot,
                label: "Copy of Title"
              };
            }
            return ot;
          }
          return null;
        })
      );
    });
    if (completedOrderTypes?.length !== Object.keys(c)?.length) {
      setCompletedOrderTypes(c);
    }
  }, [
    currentOrders,
    packetOrders,
    unplacedOrderTypes,
    completedOrderTypes,
    availableOrderTypes,
    setUnplacedOrderTypes,
    setCompletedOrderTypes
  ]);

  useEffect(() => {
    if (selectedOrders["xTitle"]) {
      setXTitleSelected(true);
    }
  }, [selectedOrders, setXTitleSelected]);

  const allowedStateTitleTypes = useCallback(
    e => {
      const state = e;
      if (!state) {
        return [];
      }
      const result = stateTitleTypes.find(
        stateTitleType => state.stateId === stateTitleType.stateId
      );
      const allowedTypes = [];
      if (result["electronicTitle"]) {
        allowedTypes.push({
          titleType: "Electronic Title",
          value: "ELECTRONIC"
        });
      }
      if (result["paperTitle"]) {
        allowedTypes.push({ titleType: "Paper Title", value: "PAPER" });
      }
      return allowedTypes;
    },
    [stateTitleTypes]
  );

  // update allowedTitleTypes so when we navigate back to this step
  // the form looks correct
  useEffect(() => {
    if (titleState && allowedTitleTypes.length === 0) {
      setAllowedTitleTypes(allowedStateTitleTypes(titleState));
    }
  }, [titleState, allowedTitleTypes, allowedStateTitleTypes]);

  const setFieldData = (args, state) => {
    const [name, data] = args;
    const field = state.fields[name];
    if (field) {
      field.data = { ...field.data, ...data };
    }
  };

  const hasTruthyValue = obj => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key]) {
        return true;
      }
    }
    return false;
  };

  const goBack = form => {
    updateSelectedOrders(form.getState().values);
    form.reset();
    stepperRef.current.prevCallback();
  };

  const goForward = () => {
    stepperRef.current.nextCallback();
  };

  const validate = () => {
    const errors = {};
    if (xTitleSelected) {
      if (!titleState) {
        errors.titleState = "Title State is required";
      }
      if (!titleType) {
        errors.titleType = "Title Type is required";
      }
    }
    return errors;
  };

  const handleStateSelection = state => {
    if (!state?.value?.stateId) {
      return;
    }
    setTitleState(state.value);
    const allowedTypes = allowedStateTitleTypes(state.value);
    setAllowedTitleTypes(allowedTypes);
    if (allowedTypes.length === 1) {
      setTitleType(allowedTypes[0]);
    } else {
      setTitleType(null);
      setAllowedTitleTypes(allowedTypes);
    }
  };

  const orderDisabledMessage = order => {
    // convert all of these to simple array format
    const selected = Object.keys(selectedOrders);
    const completed = completedOrderTypes.map(o => o?.name);
    const current = Object.keys(currentOrders);
    const conditionsMap = [
      {
        names: ["xTitle"],
        conditions: [
          {
            condition:
              mode === "create" &&
              selected.includes("loanPayoff") &&
              selected.includes("letterOfGuarantee"),
            message:
              "Cannot select Title Transfer with Loan Payoff or Letter of Guarantee"
          },
          {
            condition: mode === "create" && selected.includes("loanPayoff"),
            message: "Cannot select Title Transfer with Loan Payoff"
          },
          {
            condition:
              mode === "create" && selected.includes("letterOfGuarantee"),
            message: "Cannot select Title Transfer with Letter of Guarantee"
          },
          {
            condition:
              current.includes("letterOfGuarantee") &&
              !completed.includes("letterOfGuarantee") &&
              current.includes("loanPayoff") &&
              !completed.includes("loanPayoff"),
            message:
              "Cannot select Title Transfer when there is a pending Payoff or Letter of Guarantee order"
          },
          {
            condition:
              current.includes("loanPayoff") &&
              !completed.includes("loanPayoff"),
            message:
              "Cannot select Title Transfer when there is a pending Loan Payoff order"
          },
          {
            condition:
              current.includes("letterOfGuarantee") &&
              !completed.includes("letterOfGuarantee"),
            message:
              "Cannot select Title Transfer when there is a pending Letter of Guarantee order"
          }
        ]
      },
      {
        names: ["loanPayoff", "letterOfGuarantee", "copyOfTitle"],
        conditions: [
          {
            condition: mode === "create" && selected.includes("xTitle"),
            message: `Cannot select ${order.label} with Title Transfer`
          }
        ]
      },
      {
        names: ["any"],
        conditions: [
          {
            condition: !financed && order?.financedOnly,
            message:
              "Order Disabled: This order type is only available for financed claims"
          },
          {
            condition: financed && order?.ownedOnly,
            message:
              "Order Disabled: This order type is only available for non-financed claims"
          }
        ]
      },
      {
        names: ["lienLetter"],
        conditions: [
          {
            // block lien letter unless there is a fullfilled or cancelled LoG, if and only if order grouping is enabled
            condition:
              carrier?.orderGrouping &&
              completedOrderTypes?.find(
                o => o?.name === "letterOfGuarantee"
              ) === undefined,
            message:
              "Order Disabled: Lien Release is only available when there is a fulfilled or cancelled Letter of Guarantee order"
          }
        ]
      }
    ];

    for (let i = 0; i < conditionsMap.length; i++) {
      const type = conditionsMap[i];
      if (type.names.includes(order.name) || type.names.includes("any")) {
        for (let j = 0; j < type.conditions.length; j++) {
          const c = type.conditions[j];
          if (c?.condition) {
            return c.message;
          }
        }
      }
    }
    return null;
  };

  return (
    <div>
      <Form
        onSubmit={goForward}
        mutators={{ setFieldData }}
        initialValues={{ ...selectedOrders, ...titleTypes }}
        validate={validate}
        render={({ handleSubmit, form }) => (
          <form onSubmit={handleSubmit} className="OrderSelection">
            <>
              {unplacedOrderTypes.length === 0 && (
                <div className={"NoOrders"}>
                  <span>All available orders placed</span>
                </div>
              )}
              {[...completedOrderTypes, ...unplacedOrderTypes]?.length > 0 &&
                [...completedOrderTypes, ...unplacedOrderTypes].map(
                  orderType => {
                    return (
                      <div key={orderType?.name}>
                        {orderType && (
                          <div key={orderType.name}>
                            <Tooltip target={".order-tooltip"} />
                            <Field
                              key={orderType?.name}
                              name={orderType?.name}
                              type={"checkbox"}
                              render={({ input }) => (
                                <div
                                  className="order-tooltip"
                                  key={orderType.name}
                                  data-pr-tooltip={orderDisabledMessage(
                                    orderType
                                  )}
                                  data-pr-position="left"
                                  data-pr-at="left center"
                                  data-pr-my="right center"
                                >
                                  <div
                                    key={orderType.name}
                                    className={`OrderTypeRow ${
                                      orderDisabledMessage(orderType)
                                        ? "DisabledOrderRow"
                                        : ""
                                    }`}
                                  >
                                    <label
                                      htmlFor={orderType.name}
                                      className="OrderTypeLabel"
                                    >
                                      {completedOrderTypes.includes(orderType)
                                        ? `${orderType.label} (re-order)`
                                        : carrier?.orderGrouping &&
                                          orderType.label ===
                                            "Letter of Guarantee"
                                        ? `${orderType.label} *`
                                        : orderType.label}
                                    </label>
                                    <Checkbox
                                      inputId={orderType.name}
                                      key={orderType.name}
                                      disabled={orderDisabledMessage(orderType)}
                                      {...input}
                                      value={selectedOrders.orderType}
                                      onChange={e => {
                                        input.onChange(e);
                                        if (orderType.name === "xTitle") {
                                          setXTitleSelected(e.target.checked);
                                          setXTitleChecked(e.target.checked);
                                          // if xtitle wasn't selected at claim creation but is selected now
                                          // set the mode to full so user can input additional information
                                          // otherwise return to default mode
                                          if (
                                            e.target.checked &&
                                            mode !== "newxTitle" &&
                                            mode !== "create"
                                          ) {
                                            changeMode("newxTitle");
                                            setXTitleSelected(true);
                                          } else {
                                            revertMode();
                                          }
                                        }
                                        updateSelectedOrders({
                                          ...form.getState().values
                                        });
                                      }}
                                    />
                                  </div>
                                  <hr className={"OrderTypeSeparator"}></hr>
                                </div>
                              )}
                            />
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              {carrier?.orderGrouping && mode === "create" && (
                <div className={"NoOrders"}>
                  <span>* Includes Payoff & Title Image (as available)</span>
                </div>
              )}
              {xTitleChecked &&
                (xTitleSelected ||
                  !completedOrderTypes?.find(o => o.name === "xTitle")) && (
                  <>
                    <Field
                      name="titleState"
                      render={({ input }) => (
                        <div className="titleDropdown">
                          <span className="p-float-label">
                            <Dropdown
                              id="titleState"
                              options={states}
                              optionLabel={"stateName"}
                              {...input}
                              value={titleState}
                              onChange={e => {
                                handleStateSelection(e);
                              }}
                            />
                            <label htmlFor="titleState">
                              Titled State (Current)
                            </label>
                          </span>
                          {form.getFieldState("titleState")?.error &&
                            form.getFieldState("titleState")?.touched && (
                              <div className={"ErrorText"}>
                                {form.getFieldState("titleState")?.error}
                              </div>
                            )}
                        </div>
                      )}
                    />
                    {allowedTitleTypes.length !== 1 ? (
                      <Field
                        name="titleType"
                        render={({ input }) => (
                          <div className="titleDropdown">
                            <span className="p-float-label">
                              <Dropdown
                                id={input.name}
                                options={allowedTitleTypes}
                                optionLabel={"titleType"}
                                {...input}
                                value={titleType}
                                onChange={e => {
                                  input.onChange(e.value);
                                  setTitleType(e.value);
                                }}
                              />
                              <label htmlFor={input.name}>
                                Title Type (Current)
                              </label>
                            </span>
                            {form.getFieldState("titleType")?.error &&
                              form.getFieldState("titleType")?.touched && (
                                <div className={"ErrorText"}>
                                  {form.getFieldState("titleType")?.error}
                                </div>
                              )}
                          </div>
                        )}
                      />
                    ) : (
                      <Field
                        name="titleType"
                        render={({ input }) => (
                          <div className="titleDropdown">
                            <span className="p-float-label">
                              <InputText
                                id={input.name}
                                value={allowedTitleTypes[0].titleType}
                                disabled
                              />
                              <label htmlFor={input.name}>
                                Title Type (Current)
                              </label>
                            </span>
                          </div>
                        )}
                      />
                    )}
                  </>
                )}
              {/* {!hasTruthyValue(form.getState()?.values) &&
                mode !== "orders" && (
                  <span style={{ textAlign: "center", color: "red" }}>
                    You must select at least one order to continue.
                  </span>
                )} */}
              {/* {!financed && (
                  <span style={{ textAlign: "center", color: "red" }}>
                    New Message
                  </span>
                )} */}
              <div>
                {mode === "orders" && unplacedOrderTypes?.length > 0 && (
                  <Button
                    label={"Continue"}
                    type={"submit"}
                    onClick={() => goForward()}
                    className={"NextButton"}
                    icon={"pi pi-arrow-right"}
                    iconPos={"right"}
                    disabled={Object.keys(selectedOrders)?.length === 0}
                  />
                )}
                {mode === "create" && (
                  <div className={"ClaimNavButtons"}>
                    <Button
                      label={"Go Back"}
                      onClick={() => goBack(form)}
                      type={"button"}
                      className={"NextButton"}
                      icon={"pi pi-arrow-left"}
                      iconPos={"left"}
                    />
                    <Button
                      label={"Upload Documents"}
                      type={"submit"}
                      className={"NextButton"}
                      icon={"pi pi-arrow-right"}
                      iconPos={"right"}
                      disabled={!hasTruthyValue(form.getState()?.values)}
                    />
                  </div>
                )}
                {mode === "newxTitle" && (
                  <Button
                    label={"Upload Documents"}
                    type={"submit"}
                    className={"NextButton"}
                    icon={"pi pi-arrow-right"}
                    iconPos={"right"}
                    disabled={!hasTruthyValue(form.getState()?.values)}
                  />
                )}
              </div>
            </>
          </form>
        )}
      />
    </div>
  );
};
export default OrderSelectionList;
